import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Scale, Plus, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WeightTracker = ({ patient, date }) => {
  const [weights, setWeights] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [weightForm, setWeightForm] = useState({
    weight: '',
    unit: 'kg',
    notes: ''
  });

  useEffect(() => {
    if (patient?.id) {
      fetchWeights();
    }
  }, [patient?.id]);

  const fetchWeights = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/weights/${patient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeights(response.data);
    } catch (error) {
      console.error('Error fetching weights:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!weightForm.weight) {
      toast.error('Please enter a weight');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/weights`, {
        patient_id: patient.id,
        date: date,
        weight: parseFloat(weightForm.weight),
        unit: weightForm.unit,
        notes: weightForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Weight logged!');
      setShowDialog(false);
      setWeightForm({ weight: '', unit: 'kg', notes: '' });
      fetchWeights();
    } catch (error) {
      toast.error('Failed to log weight');
    } finally {
      setLoading(false);
    }
  };

  const latestWeight = weights[0];
  const previousWeight = weights[1];
  
  const getWeightChange = () => {
    if (!latestWeight || !previousWeight) return null;
    const change = latestWeight.weight - previousWeight.weight;
    return {
      value: Math.abs(change).toFixed(1),
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'same',
      unit: latestWeight.unit
    };
  };

  const weightChange = getWeightChange();

  if (!patient) return null;

  return (
    <>
      <div className="card h-full" data-testid="weight-tracker">
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setShowDialog(true)}
              className="btn btn-primary text-xs sm:text-sm px-2 sm:px-3 py-1 flex items-center gap-1 flex-shrink-0"
              data-testid="add-weight-btn"
            >
              <Plus size={12} className="sm:w-[14px] sm:h-[14px]" />
              <span>Log</span>
            </button>
            <Scale size={18} className="flex-shrink-0 sm:w-5 sm:h-5" style={{ color: '#A2DAD3' }} />
            <h3 className="text-base sm:text-lg font-semibold truncate" style={{ color: '#A2DAD3' }}>Weight</h3>
          </div>
        </div>

        {latestWeight ? (
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2">
            <div className="min-w-0">
              <p className="text-2xl sm:text-3xl font-bold" style={{ color: '#333' }}>
                {latestWeight.weight}
                <span className="text-sm sm:text-lg font-normal text-gray-500 ml-1">
                  {latestWeight.unit}
                </span>
              </p>
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1 truncate">
                Last: {new Date(latestWeight.date).toLocaleDateString()}
              </p>
            </div>
            
            {weightChange && (
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs sm:text-sm w-fit flex-shrink-0 ${
                weightChange.direction === 'up' 
                  ? 'bg-red-50 text-red-600' 
                  : weightChange.direction === 'down'
                  ? 'bg-green-50 text-green-600'
                  : 'bg-gray-50 text-gray-600'
              }`}>
                {weightChange.direction === 'up' && <TrendingUp size={12} className="sm:w-[14px] sm:h-[14px]" />}
                {weightChange.direction === 'down' && <TrendingDown size={12} className="sm:w-[14px] sm:h-[14px]" />}
                {weightChange.direction === 'same' && <Minus size={12} className="sm:w-[14px] sm:h-[14px]" />}
                <span>{weightChange.value} {weightChange.unit}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-3 sm:py-4">
            <p className="text-gray-500 text-xs sm:text-sm">No weight recorded</p>
            <button
              onClick={() => setShowDialog(true)}
              className="text-xs sm:text-sm mt-2"
              style={{ color: '#A2DAD3' }}
            >
              Log first weight
            </button>
          </div>
        )}
      </div>

      {/* Weight Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ color: '#A2DAD3' }}>Log Weight</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={weightForm.weight}
                  onChange={(e) => setWeightForm({ ...weightForm, weight: e.target.value })}
                  placeholder="Enter weight"
                  className="w-full"
                  data-testid="weight-input"
                />
              </div>
              <div className="w-24">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unit
                </label>
                <select
                  value={weightForm.unit}
                  onChange={(e) => setWeightForm({ ...weightForm, unit: e.target.value })}
                  className="w-full h-[42px] rounded-lg border border-gray-200 px-3"
                  data-testid="weight-unit"
                >
                  <option value="kg">kg</option>
                  <option value="lbs">lbs</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={weightForm.notes}
                onChange={(e) => setWeightForm({ ...weightForm, notes: e.target.value })}
                placeholder="Any notes..."
                className="w-full h-20 resize-none"
                data-testid="weight-notes"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDialog(false)}
                className="btn flex-1"
                style={{ backgroundColor: '#e5e5e5', color: '#333' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary flex-1"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WeightTracker;
